import type { MentionRow } from './generator';

/**
 * The date index: rows sorted ascending by day, plus a binary search.
 *
 * This is deliberately the *cheap half* of an index and nothing more. At 4,000
 * rows a full scan costs ~0.2ms while Recharts repainting six SVGs costs 10-40ms,
 * so an elaborate multi-dimension index would be ceremony that buys nothing
 * measurable. The date predicate earns its structure anyway, for two reasons: it
 * is by far the most selective (a 7-day window keeps 7/400 of the corpus), and
 * it is the one that runs on every single query.
 *
 * Please do not "optimise" this into a full inverted index. The remaining five
 * predicates are `Set.has()` calls over an already-small slice.
 */
export type RowIndex = {
  /** Every row, ascending by `day`. */
  rows: readonly MentionRow[];
  /** Ascending `day` values, positionally aligned with `rows`. */
  days: readonly number[];
};

export function buildRowIndex(rows: readonly MentionRow[]): RowIndex {
  const sorted = [...rows].sort((a, b) => a.day - b.day);
  return { rows: sorted, days: sorted.map((row) => row.day) };
}

/**
 * The first position whose value is >= `target`, or `days.length` if none is.
 *
 * Standard lower-bound: the half-open interval `[lo, hi)` shrinks until it is
 * empty, and `lo` is the answer. Written out rather than pulled from a library
 * because the off-by-one here is the kind that produces a chart missing its
 * first day, which reads as a data problem rather than a search bug.
 */
export function lowerBound(days: readonly number[], target: number): number {
  let lo = 0;
  let hi = days.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (days[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * The rows falling in `[from, to]` **inclusive of both ends**, as a slice.
 *
 * Inclusive because a date range picked in the UI includes the day the user
 * clicked last; the upper bound is therefore `lowerBound(to + 1)`, not
 * `lowerBound(to)`. Both bounds are day numbers (local midnight), so `to + 1` is
 * safe only because nothing between two midnights is ever stored.
 */
export function rowsInRange(index: RowIndex, from: number, to: number): readonly MentionRow[] {
  if (to < from) return [];
  const start = lowerBound(index.days, from);
  const end = lowerBound(index.days, to + 1);
  return index.rows.slice(start, end);
}
